import React from 'react';
import '../components/styles/products.css';

const makeupDefinitions = {
  definitions: [
    {
      title: "Glam Makeup Look",
      description:
        "A bold, flawless style that enhances features with dramatic elements like defined eyes, sculpted cheeks, and a perfect pout, creating a glamorous, high-impact appearance."
    },
    {
      title: "Soft Glam Makeup Look",
      description:
        "A natural, understated style that enhances your features with subtle tones and light application, creating a fresh, effortless, and radiant appearance."
    },
    {
      title: "Cultural Bridal Makeup Look",
      description:
        "A stunning, traditional style that reflects the bride’s heritage, incorporating elements such as bold colors, intricate detailing, and symbolic touches to celebrate her cultural identity on her special day."
    },
    {
      title: "North American Bridal Makeup Look",
      description:
        "A timeless, elegant style that enhances the bride’s natural beauty with soft, romantic tones, flawless skin, and subtle details, creating a sophisticated and classic appearance for her big day."
    },
    {
      title: "Smokey Eye Makeup Look",
      description:
        "A dramatic, sultry style that blends dark, bold eyeshadow with soft, blended edges to create a smoldering, mysterious effect around the eyes."
    },
    {
      title: "Halo Eye Makeup Look",
      description:
        "A bold and striking style where the eyelids are accentuated with a lighter shade in the center, surrounded by darker shades on the outer and inner corners, creating a 'halo' effect that makes the eyes appear larger and more vibrant."
    },
    {
      title: "Gradient Eye Makeup Look",
      description:
        "A seamless, blended style where the eyeshadow transitions smoothly from light to dark shades, creating a soft, multi-dimensional effect that enhances the eyes with depth and dimension."
    }
  ]
};

const productCategories = {
  face: {
    title: "Face Products",
    items: [
      { name: "Primer", description: "Prepares and smooths the skin for makeup application." },
      { name: "Foundation", description: "Provides an even skin tone and coverage." },
      { name: "Concealer", description: "Covers blemishes, dark circles, or imperfections." },
      { name: "Setting Powder", description: "Sets foundation and concealer, controls shine." },
      { name: "Setting Spray", description: "Locks makeup in place for long-lasting wear." },
      { name: "Blush", description: "Adds color to the cheeks and enhances the complexion." },
      { name: "Bronzer", description: "Adds warmth and dimension to the face." },
      { name: "Highlighter", description: "Gives the skin a luminous, glowing finish." },
      { name: "Contour", description: "Sculpts and defines the face (often in powder or cream form)." }
    ]
  },
  eyes: {
    title: "Eye Products",
    items: [
      { name: "Eyeshadow", description: "Available in powder, cream, or liquid form to add color to the eyelids." },
      { name: "Eyeshadow Primer", description: "Ensures eyeshadow stays in place and enhances color payoff." },
      { name: "Eyebrow Products", description: "Fills in, shapes, and defines eyebrows." },
      { name: "Eyeliner", description: "Defines the eyes, available in pencil, liquid, gel, or cream." },
      { name: "Mascara", description: "Lengthens, thickens, and curls the lashes." },
      { name: "False Lashes", description: "Adds volume or length to natural lashes for dramatic effect." }
    ]
  },
  lips: {
    title: "Lip Products",
    items: [
      { name: "Lip Balm", description: "Moisturizes and softens lips before applying color." },
      { name: "Lip Liner", description: "Defines the lips and prevents lipstick from feathering." },
      { name: "Lipstick", description: "Available in various finishes like matte, satin, and glossy." },
      { name: "Lip Gloss", description: "Adds shine and a subtle tint or sparkle to lips." },
      { name: "Liquid Lipstick", description: "Long-lasting, often matte finish." },
      { name: "Lip Tint", description: "Lightweight, long-wearing color for the lips." }
    ]
  },
  toolsAndBrushes: {
    title: "Tools & Brushes",
    items: [
      { name: "Foundation Brush", description: "Used to apply foundation smoothly and evenly." },
      { name: "Concealer Brush", description: "Precision brush for applying concealer." },
      { name: "Blending Sponge (Beauty Blender)", description: "Used to blend liquid or cream products seamlessly." },
      { name: "Powder Brush", description: "Large, fluffy brush for setting powder or bronzer." },
      { name: "Blush Brush", description: "Applies blush to the cheeks." },
      { name: "Contour Brush", description: "Angled brush for precise contouring." },
      { name: "Eyeshadow Brushes", description: "Various shapes and sizes for applying and blending eyeshadows." },
      { name: "Eyeliner Brush", description: "Used for applying gel or cream eyeliner." },
      { name: "Lip Brush", description: "Precision brush for applying lip color." },
      { name: "Eyebrow Brush", description: "For shaping and filling brows." },
      { name: "Tweezers", description: "For shaping and grooming brows or applying false lashes." }
    ]
  },
  skincarePrep: {
    title: "Skincare Prep",
    items: [
      { name: "Face Cleanser", description: "Prepares skin by cleansing before makeup." },
      { name: "Toner", description: "Refreshes and balances skin's pH." },
      { name: "Moisturizer", description: "Hydrates the skin before makeup application." },
      { name: "Face Masks", description: "Used for deep cleansing, hydration, or exfoliation." },
      { name: "Eye Cream", description: "Targeted treatment for under-eye areas." }
    ]
  },
  additionalProducts: {
    title: "Additional Products",
    items: [
      { name: "Color Correctors", description: "Used to neutralize specific skin concerns (e.g., redness, dark circles)." },
      { name: "Face Oil", description: "Adds moisture and glow, often used for dry skin or to create a dewy finish." },
      { name: "Brow Gel", description: "Holds brow hairs in place for a groomed look." },
      { name: "Makeup Remover", description: "Cleanses makeup from the skin at the end of the day." },
      { name: "Makeup Wipes", description: "Convenient wipes for quick makeup removal." },
      { name: "Brush Cleaner", description: "Cleans makeup brushes to maintain hygiene and performance." }
    ]
  }
};

const Products = () => {
  return (
    <div className="products-container">
      <h2 className="products-title">Educational Guide</h2>

      {/* Definitions Section */}
      <div className="definitions-box">
        <h3 className="definitions-title">Makeup Definitions</h3>
        <ul className="definitions-list">
          {makeupDefinitions.definitions.map((def, index) => (
            <li key={index} className="definition-item">
              <h4 className="definition-title">{def.title}</h4>
              <p className="definition-description">{def.description}</p>
            </li>
          ))}
        </ul>
      </div>

      {/* Products Section */}
      <div className="products-grid">
        {Object.entries(productCategories).map(([key, category]) => (
          <div key={key} className={`product-category ${key}-products`}>
            <h3 className="category-title">{category.title}</h3>
            <ul className="product-list">
              {category.items.map((item, index) => (
                <li key={index} className="product-item">
                  <span className="product-name">{item.name}:</span>
                  <span className="product-description">{item.description}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Products;
